import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { navigationContent } from "../../data/navigation";
import { useNavigationHandlers } from "./Navigation.handlers";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { useMobileOptimization } from "../../hooks/useMobileOptimization";
import type { NavigationProps } from "./Navigation.types";
import "./Navigation.styles.css";

export const Navigation: React.FC<NavigationProps> = ({ currentPath }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const prefersReducedMotion = useReducedMotion();
  const mobileOptimization = useMobileOptimization();
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const {
    handleMenuClick,
    handleBookMeetingClick,
    handleMobileMenuToggle,
    mobileMenuOpen,
  } = useNavigationHandlers();

  const currentRoute = currentPath || location.pathname;

  // Determine if animations should be simplified
  const shouldSimplifyAnimations =
    prefersReducedMotion || mobileOptimization.shouldSimplifyAnimations;

  // Enhanced scroll behavior for premium navigation effects
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY ? "down" : "up";

      // Hide navigation when scrolling down, show when scrolling up
      const shouldHide =
        direction === "down" && currentScrollY > 100 && !mobileMenuOpen;

      setScrollDirection(direction);
      setIsVisible(!shouldHide);
      setLastScrollY(currentScrollY);
    };

    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledHandleScroll, { passive: true });
    return () => window.removeEventListener("scroll", throttledHandleScroll);
  }, [lastScrollY, mobileMenuOpen]);

  const renderDesktopMenu = () => (
    <Box
      className="navigation-menu-desktop"
      component="nav"
      role="navigation"
      aria-label="Main navigation"
    >
      {navigationContent.menuItems.map((item, index) => (
        <a
          key={item.path}
          href={item.path}
          onClick={(e) => {
            e.preventDefault();
            handleMenuClick(item.path);
          }}
          className={`navigation-menu-item ${
            currentRoute === item.path ? "active" : ""
          } ${!shouldSimplifyAnimations ? "premium-hover-effect" : ""}`}
          style={
            {
              "--item-index": index,
              "--stagger-delay": `${index * 0.1}s`,
            } as React.CSSProperties
          }
          aria-current={currentRoute === item.path ? "page" : undefined}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleMenuClick(item.path);
            }
          }}
        >
          <span className="menu-item-text">{item.label}</span>
          <span className="menu-item-underline" aria-hidden="true"></span>
          <span className="menu-item-glow" aria-hidden="true"></span>
        </a>
      ))}
      <Button
        variant="contained"
        className={`navigation-cta-button premium-cta-button ${
          !shouldSimplifyAnimations ? "premium-button-animation" : ""
        }`}
        onClick={handleBookMeetingClick}
        aria-label="Book a meeting with Dr. Graeme Keith"
      >
        <span className="button-text">{navigationContent.ctaButton.text}</span>
        <span className="button-shine" aria-hidden="true"></span>
        <span className="button-glow" aria-hidden="true"></span>
      </Button>
    </Box>
  );

  const renderMobileMenu = () => (
    <Drawer
      anchor="top"
      open={mobileMenuOpen}
      onClose={handleMobileMenuToggle}
      className="navigation-mobile-menu"
      PaperProps={{
        sx: {
          top: 64, // Position below the AppBar
          backgroundColor: "#ffffff",
        },
      }}
      aria-label="Mobile navigation menu"
    >
      <Box role="navigation" aria-label="Mobile navigation">
        <List>
          {navigationContent.menuItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <a
                href={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick(item.path);
                }}
                className={`navigation-mobile-menu-item ${
                  currentRoute === item.path ? "active" : ""
                }`}
                aria-current={currentRoute === item.path ? "page" : undefined}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleMenuClick(item.path);
                  }
                }}
              >
                {item.label}
              </a>
            </ListItem>
          ))}
        </List>
        <Box className="navigation-mobile-cta">
          <Button
            variant="contained"
            fullWidth
            className="navigation-cta-button"
            onClick={handleBookMeetingClick}
            aria-label="Book a meeting with Dr. Graeme Keith"
          >
            {navigationContent.ctaButton.text}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="sticky"
        className={`navigation-root premium-navigation ${
          scrollDirection === "down" ? "scroll-down" : "scroll-up"
        } ${
          !isVisible ? "hidden" : ""
        } ${!shouldSimplifyAnimations ? "glass-effect" : ""}`}
        elevation={0}
        style={{
          transform:
            !shouldSimplifyAnimations && !isVisible
              ? "translateY(-100%)"
              : "translateY(0)",
          transition: !shouldSimplifyAnimations
            ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            : "none",
        }}
      >
        <Toolbar
          className={`navigation-toolbar premium-toolbar`}
          sx={{
            minHeight: "70px !important",
            height: "70px !important",
            maxHeight: "70px !important",
            py: "0px !important",
          }}
        >
          {/* Enhanced Logo */}
          <Box
            sx={{
              display: "flex !important",
              alignItems: "center !important",
              justifyContent: "center !important",
              height: "70px !important",
              py: "2px !important",
            }}
          >
            <Box
              component="img"
              src={navigationContent.logo}
              alt="Stochastic ApS"
              onClick={() => handleMenuClick("/")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleMenuClick("/");
                }
              }}
              aria-label="Stochastic ApS - Return to home page"
              sx={{
                height: "50px !important",
                width: "auto !important",
                maxHeight: "50px !important",
                maxWidth: "none !important",
                minHeight: "50px !important",
                display: "block !important",
                objectFit: "contain !important",
                cursor: "pointer",
                filter: "brightness(0) invert(1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  filter:
                    "brightness(0) invert(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3))",
                },
              }}
            />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && renderDesktopMenu()}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ marginLeft: "auto" }}>
              <IconButton
                edge="end"
                className={`navigation-mobile-menu-button premium-mobile-button ${
                  !shouldSimplifyAnimations ? "mobile-button-animation" : ""
                }`}
                onClick={handleMobileMenuToggle}
                aria-label={
                  mobileMenuOpen
                    ? "Close navigation menu"
                    : "Open navigation menu"
                }
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-navigation-menu"
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      {isMobile && renderMobileMenu()}
    </>
  );
};

export default Navigation;
